import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';
import topicsList from '../../Topics/Data/topicslist';
import './planning-a-lesson.css';

const PlanningALesson = () => {
  const title = 'Planning a Lesson';
  const image = '/Images/1person2.webp';
  const intro = 'A practical, modified PPP framework for online and classroom ESL lessons. This structure assumes most students have studied grammar in school — the lesson activates, reviews, and gets them talking.';

  const seoContent = [];

  const stages = [
    {
      number: 1,
      title: 'Small Talk',
      time: '3–5 min',
      colorClass: 'stage-1',
      content: (
        <>
          <p><strong>Every lesson starts here.</strong> Natural, unscripted "how are you" chat — weekend, weather, work, a news item they saw. This is not a warm-up activity; it's relationship building and a diagnostic listening sample. Note errors silently for later.</p>
          <p className="tip"><strong>Tip:</strong> Keep a "small talk bank" of 20+ openers so it never feels routine.</p>
        </>
      )
    },
    {
      number: 2,
      title: 'Warmer',
      time: '5–8 min',
      colorClass: 'stage-2',
      content: (
        <p>A short game or activity that <strong>introduces the topic or target language</strong> without announcing it. Examples: Taboo with key vocab, "Two Truths, One Lie" using the grammar point, picture prompt speculation, quick poll/debate on a controversial statement. Energy up, schemata activated.</p>
      )
    },
    {
      number: 3,
      title: 'Language Point',
      time: 'A1–B1: 15–20 min  |  B1+: 5–10 min',
      colorClass: 'stage-3',
      content: (
        <>
          <p><strong>Review, don't present.</strong> One grammar structure or functional exponent relevant to the topic.</p>
          <ul>
            <li><strong>Inductive:</strong> Students spot the pattern in 3–4 sentences from the warmer or a mini-text</li>
            <li><strong>Clarify:</strong> Form, meaning, pronunciation — 2 minutes max</li>
            <li><strong>Controlled practice:</strong> One quick exercise (gap-fill, transformation, matching)</li>
            <li><strong>Semi-controlled:</strong> Personalized sentences, mini-role-play</li>
          </ul>
          <p>For B1+, skip the exercise if they nail it — move straight to personalized use.</p>
        </>
      )
    },
    {
      number: 4,
      title: 'Trivia Quiz',
      time: '8–12 min',
      colorClass: 'stage-4',
      content: (
        <>
          <p><strong>My PPP modification.</strong> A pub-quiz style round on the topic. Serves three purposes:</p>
          <ul>
            <li>Introduces/reviews <strong>topic vocabulary</strong> in context</li>
            <li>Provides <strong>content for discussion</strong> (surprising facts = opinions)</li>
            <li>Gamifies the lesson — low stakes, high engagement</li>
          </ul>
          <p>Format: 5–7 questions, multiple choice or short answer. Mix fact, opinion, and "guess the statistic" items. Students play individually or in pairs. Debrief each answer — <em>that's</em> where the language happens.</p>
        </>
      )
    },
    {
      number: 5,
      title: 'Vocabulary Focus',
      time: '5–10 min',
      colorClass: 'stage-5',
      content: (
        <>
          <p>Can come <strong>before or after the quiz</strong> depending on flow. Pull 8–12 high-yield items from the quiz, reading, or video. Focus on:</p>
          <ul>
            <li>Collocations (not single words)</li>
            <li>Phrasal verbs / idioms relevant to topic</li>
            <li>Register shifts (formal ↔ informal)</li>
          </ul>
          <p>Quick activities: match, categorize, rank by usefulness, create personal examples.</p>
        </>
      )
    },
    {
      number: 6,
      title: 'Reading OR Video',
      time: '10–15 min',
      colorClass: 'stage-6',
      content: (
        <>
          <p><strong>Choose one.</strong> Time rarely allows both meaningfully.</p>
          <ul>
            <li><strong>Reading:</strong> Authentic article, blog post, infographic. Tasks: gist → detail → inference → reaction.</li>
            <li><strong>Video:</strong> 2–4 min clip (TED-Ed, news, interview, short film). Tasks: predict → watch for gist → watch for detail → language mining.</li>
          </ul>
          <p>For B1+: skip comprehension questions. Use the content as a <strong>discussion springboard</strong> — "What surprised you?" "Do you agree with X?" "How does this compare to your context?"</p>
        </>
      )
    },
    {
      number: 7,
      title: 'Discussion Questions',
      time: 'A1–B1: 10–15 min  |  B1+: 20–40 min',
      colorClass: 'stage-7',
      content: (
        <>
          <p><strong>The core of the lesson, especially B1+.</strong> 6–10 questions progressing from personal → abstract → critical. Structure:</p>
          <ol>
            <li><strong>Experience:</strong> "Have you ever...?" "What's your experience with...?"</li>
            <li><strong>Opinion:</strong> "Do you think...?" "Is it better to...?"</li>
            <li><strong>Hypothetical:</strong> "What would you do if...?" "How might this change in 10 years?"</li>
            <li><strong>Critical/Creative:</strong> "Design a policy for...", "Convince someone who disagrees..."</li>
          </ol>
          <p><strong>Management:</strong> Pairs → small groups → whole class report-back. Rotate partners. Teacher floats, notes language for delayed feedback. End with 2-min error correction spotlight.</p>
        </>
      )
    },
  ];

  const modes = [
    {
      type: 'learning',
      title: 'A1–B1: Learning & Consolidation Mode',
      items: [
        'Students still acquiring core grammar and vocabulary',
        'Need structured practice, clear models, correction',
        'Language point stage is <strong>longer and more explicit</strong>',
        'Discussion questions are simpler, more guided',
        'Reading/video with comprehension checks essential'
      ]
    },
    {
      type: 'conversation',
      title: 'B1+: Conversation & Fluency Mode',
      items: [
        'Grammar knowledge is passive; activation is the goal',
        'Language point stage is <strong>brief review</strong> (5–10 min)',
        'Discussion questions are the <strong>meat of the lesson</strong> (25–40 min)',
        'Reading/video serves as discussion springboard, not comprehension exercise',
        'Correction is delayed, focused on fossilized errors'
      ]
    }
  ];

  const timingRows = [
    { stage: 'Small Talk', a1b1: '3 min', b1plus: '3 min' },
    { stage: 'Warmer', a1b1: '5 min', b1plus: '5 min' },
    { stage: 'Language Point', a1b1: '15–20 min', b1plus: '5–10 min' },
    { stage: 'Trivia Quiz', a1b1: '10 min', b1plus: '10 min' },
    { stage: 'Vocabulary', a1b1: '7 min', b1plus: '7 min' },
    { stage: 'Reading / Video', a1b1: '10 min', b1plus: '10 min' },
    { stage: 'Discussion', a1b1: '10–15 min', b1plus: '20–40 min' },
  ];

  const checklistItems = [
    'Topic chosen and framed as question/problem',
    'Language point selected (1 grammar + 1 function max)',
    'Trivia quiz written (5–7 questions with debrief notes)',
    'Vocab list extracted (8–12 collocations/phrasal verbs)',
    'Reading OR video sourced, tasks prepared',
    'Discussion questions laddered (experience → opinion → hypothetical → critical)',
    'Mode identified (A1–B1 learning vs B1+ conversation) — adjust timings',
    'Error correction plan: what to correct, when, how',
  ];

  // Find relevant existing lesson plans to use as worked examples
  const workedExamples = [
    topicsList.find(t => t.id === 'workfuture'),
    topicsList.find(t => t.id === 'foodculture'),
    topicsList.find(t => t.id === 'sustainability'),
  ].filter(Boolean);

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={[]}
      seoTitle="Planning an ESL Lesson: Modified PPP Framework for Conversation-Focused Classes"
      seoDescription="Step-by-step lesson planning guide using a modified PPP structure: small talk, warmer, language review, trivia quiz, vocab, reading/video, discussion questions. Includes A1-B1 vs B1+ teaching approach breakdown."
    >
      <div className="teach-content">
        {/* Overview Hero */}
        <div className="overview-box">
          <h2>Modified PPP for Modern ESL</h2>
          <p>
            The classic PPP (Presentation → Practice → Production) was designed for teaching new grammar to beginners. 
            Most of our students — especially B1 and above — have already "learned" the grammar in school. 
            They don't need presentation; they need <strong>activation, review, and confident production</strong>.
          </p>
          <p>
            This framework adapts PPP into a <strong>topic-driven, conversation-first flow</strong> that works for 
            45–90 minute lessons. Each stage has a clear purpose, timing, and color-coded identity below.
          </p>
        </div>

        {/* Two Teaching Modes */}
        <section className="content-section">
          <h2>The Two Teaching Modes</h2>
          <p>Before planning, identify which mode your student needs. This determines your pacing, depth, and focus.</p>
          <div className="mode-comparison">
            {modes.map((mode, i) => (
              <div key={i} className={`mode-card ${mode.type}`}>
                <h3>{mode.title}</h3>
                <ul>
                  {mode.items.map((item, j) => (
                    <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Lesson Flow Timeline */}
        <section className="content-section">
          <h2>Lesson Flow: 7 Stages</h2>
          <div className="lesson-flow-intro">
            <p>Each stage builds on the last. Click through the timeline to see details, timing, and teaching tips for each step.</p>
          </div>
          {stages.map((stage, i) => (
            <article key={i} className={`stage ${stage.colorClass}`}>
              <div className="stage-number">{stage.number}</div>
              <div className="stage-content">
                <h3>
                  <span>{stage.title}</span>
                  <span style={{marginLeft: 'auto', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8'}}>{stage.time}</span>
                </h3>
                {stage.content}
              </div>
            </article>
          ))}
        </section>

        {/* Timing Cheat Sheet */}
        <section className="content-section">
          <h2>Timing Cheat Sheet (60-min Lesson)</h2>
          <table className="timing-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>A1–B1</th>
                <th>B1+</th>
              </tr>
            </thead>
            <tbody>
              {timingRows.map((row, i) => (
                <tr key={i}>
                  <td>{row.stage}</td>
                  <td>{row.a1b1}</td>
                  <td>{row.b1plus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Worked Examples - Real Lesson Plans */}
        <section className="content-section worked-examples">
          <h2>Worked Examples <span style={{fontSize: '0.9rem', fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.75rem'}}>(from existing lesson plans)</span></h2>
          <div className="example-grid">
            {workedExamples.map((ex, i) => (
              <article key={ex.id} className="example-card">
                <h3>{ex.title}</h3>
                <p className="example-level">{ex.level}</p>
                <p className="example-grammar"><strong>Language Point:</strong> {ex.languagepoint}</p>
                <p className="example-vocab"><strong>Category:</strong> {ex.category}</p>
                <p className="example-task"><strong>Description:</strong> {ex.description}</p>
                <a href={`/lesson-plans/${ex.id}`} className="activity-link-item" style={{marginTop: '1rem', display: 'inline-flex'}}>
                  View Lesson Plan
                </a>
              </article>
            ))}
          </div>
          {workedExamples.length === 0 && (
            <p style={{color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem'}}>
              No example lessons found. Add lessons to the topics list to see them here.
            </p>
          )}
        </section>

        {/* Planning Checklist */}
        <section className="content-section checklist">
          <h2>Planning Checklist</h2>
          <ul>
            {checklistItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
    </TeachEnglishPageLayout>
  );
};

export default PlanningALesson;